import { getAuthToken } from "@/lib/auth";
import type { Translate } from "@/i18n/LanguageContext";
import type { MonthOption } from "./types";
import type { OrgUnitHierarchy } from "@/components/org-unit/OrgUnitTree";

export const API_BASE_URL = `${import.meta.env.VITE_API_BASE_URL}/api/v1`;
export const DEFAULT_INDICATOR = "Malaria Positivity Rate";

export const _ethiopianYear = () => {
  const now = new Date();
  const m = now.getMonth() + 1;
  const y = now.getFullYear();
  const d = now.getDate();
  return m > 9 || (m === 9 && d >= 11) ? String(y - 7) : String(y - 8);
};

export const _ethiopianMonth = () => {
  const now = new Date();
  const gd = now.getDate();
  const gm = now.getMonth() + 1;
  let m = gm < 9 ? gm + 4 : gm - 8;
  if (gd < 11) { m -= 1; if (m < 1) m += 12; }
  return String(m).padStart(2, "0");
};

const _ethiopianYearFromGregorian = (gm: number, gy: number) =>
  gm >= 9 ? gy - 7 : gy - 8;

export const _ethiopianYearMonth = () => {
  const now = new Date();
  const gm = now.getMonth() + 1;
  const gy = now.getFullYear();
  const gd = now.getDate();
  let em = gm < 9 ? gm + 4 : gm - 8;
  if (gd < 11) { em -= 1; if (em < 1) em += 12; }
  return {
    y: String(_ethiopianYearFromGregorian(gm, gy)),
    m: String(em).padStart(2, "0"),
  };
};

export const _lastEthiopianYearMonth = () => {
  const now = new Date();
  const gm = now.getMonth() + 1;
  const gy = now.getFullYear();
  const gd = now.getDate();
  let em = gm < 9 ? gm + 4 : gm - 8;
  if (gd < 11) { em -= 1; if (em < 1) em += 12; }
  em -= 1;
  let ey = _ethiopianYearFromGregorian(gm, gy);
  if (em < 1) { em += 12; ey -= 1; }
  return {
    y: String(ey),
    m: String(em).padStart(2, "0"),
  };
};

export const getLast12MonthsWindow = () => {
  const currentYear = Number(_ethiopianYear());
  const currentMonth = Number(_ethiopianMonth());
  const currentTotal = currentYear * 12 + (currentMonth - 1);
  const endTotal = currentTotal - 1;
  const startTotal = currentTotal - 12;
  const endYear = Math.floor(endTotal / 12);
  const endMonth = (endTotal % 12) + 1;
  const startYear = Math.floor(startTotal / 12);
  const startMonth = (startTotal % 12) + 1;

  return {
    startYear: String(startYear),
    startMonth: String(startMonth).padStart(2, "0"),
    endYear: String(endYear),
    endMonth: String(endMonth).padStart(2, "0"),
  };
};

export const DEFAULT_YEAR = _lastEthiopianYearMonth().y;
export const DEFAULT_MONTH = _lastEthiopianYearMonth().m;
export const NEW_DATASET_DEFAULT_YEAR = _lastEthiopianYearMonth().y;
export const NEW_DATASET_DEFAULT_MONTH = _lastEthiopianYearMonth().m;

export const FALLBACK_MONTHS: MonthOption[] = [
  { value: "01", label: "Meskerem" },
  { value: "02", label: "Tikimt" },
  { value: "03", label: "Hidar" },
  { value: "04", label: "Tahsas" },
  { value: "05", label: "Tir" },
  { value: "06", label: "Yekatit" },
  { value: "07", label: "Megabit" },
  { value: "08", label: "Miazia" },
  { value: "09", label: "Ginbot" },
  { value: "10", label: "Sene" },
  { value: "11", label: "Hamle" },
  { value: "12", label: "Nehase" },
];

export const authHeaders = () => {
  const token = getAuthToken();
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

export const severityClass = (severity?: string) => {
  const value = String(severity || "").toLowerCase();
  if (value === "high") return "bg-red-50 text-red-700 border-red-200";
  if (value === "medium") return "bg-amber-50 text-amber-800 border-amber-200";
  if (value === "new_reporting") return "bg-cyan-50 text-cyan-700 border-cyan-200";
  return "bg-slate-50 text-slate-700 border-slate-200";
};

export const appendHierarchyParams = (params: URLSearchParams, hierarchy: OrgUnitHierarchy) => {
  if (hierarchy.region) params.set("region", hierarchy.region);
  if (hierarchy.zone) params.set("zone", hierarchy.zone);
  if (hierarchy.woreda) params.set("woreda", hierarchy.woreda);
  if (hierarchy.phcu) params.set("phcu", hierarchy.phcu);
  if (hierarchy.level6) params.set("level6", hierarchy.level6);
  if (hierarchy.level7) params.set("level7", hierarchy.level7);
};

export const formatPeriodLabel = (period?: string) => {
  const value = String(period || "");
  if (value.length !== 6) return value || "-";
  return `${value.slice(0, 4)}-${value.slice(4, 6)}`;
};

export const formatAnomalyType = (anomalyType?: string, t?: Translate) => {
  if (!anomalyType) return t ? t("dash.type.anomaly") : "Anomaly";
  const normalized = String(anomalyType).toLowerCase();
  if (!t) {
    if (normalized === "missing_data") return "Missing data";
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  }
  if (normalized === "missing_data") return t("dash.type.missingData");
  if (normalized === "trend") return t("dash.type.trend");
  if (normalized === "rolling") return t("dash.type.rolling");
  if (normalized === "seasonal") return t("dash.type.seasonal");
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

export const formatSeverity = (severity?: string, t?: Translate) => {
  const value = String(severity || "").toLowerCase();
  if (!t) return value;
  if (value === "high") return t("dash.severity.high");
  if (value === "medium") return t("dash.severity.medium");
  if (value === "low") return t("dash.severity.low");
  if (value === "new_reporting") return t("dash.severity.newReporting");
  return value;
};

export const toFiniteNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const normalized =
    typeof value === "string"
      ? value.replace(/,/g, "").replace(/%/g, "").trim()
      : value;
  if (normalized === "") return null;
  const numeric = typeof normalized === "number" ? normalized : Number(normalized);
  return Number.isFinite(numeric) ? numeric : null;
};

export const formatRelativeTime = (timestamp?: number | null, t?: Translate) => {
  if (!timestamp) return "";
  const elapsed = Date.now() - timestamp;
  const translate = t ?? ((key: Parameters<Translate>[0], params?: Record<string, string | number>) => {
    const fallback: Record<string, string> = {
      "dash.time.justNow": "just now",
      "dash.time.minuteAgo": "{n} min ago",
      "dash.time.minutesAgo": "{n} mins ago",
      "dash.time.hourAgo": "{n} hr ago",
      "dash.time.hoursAgo": "{n} hrs ago",
      "dash.time.dayAgo": "{n} day ago",
      "dash.time.daysAgo": "{n} days ago",
    };
    const template = fallback[key] ?? key;
    return params ? template.replace(/\{(\w+)\}/g, (_m, name: string) => String(params[name] ?? "")) : template;
  });
  if (elapsed < 60_000) return translate("dash.time.justNow");
  const minutes = Math.floor(elapsed / 60_000);
  if (minutes < 60) return translate(minutes === 1 ? "dash.time.minuteAgo" : "dash.time.minutesAgo", { n: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return translate(hours === 1 ? "dash.time.hourAgo" : "dash.time.hoursAgo", { n: hours });
  const days = Math.floor(hours / 24);
  return translate(days === 1 ? "dash.time.dayAgo" : "dash.time.daysAgo", { n: days });
};

export const anomalyColor = (severity?: string) => {
  const value = String(severity || "").toLowerCase();
  if (value === "high") return "#dc2626";
  if (value === "medium") return "#d97706";
  return "#64748b";
};
